import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { ArrowLeftRight, X } from 'lucide-react';
import { deviceAPI } from '../../features/devices/deviceAPI';
import './DeviceSwapModal.css';

export default function DeviceSwapModal({ device, user, onRemove, onClose, onSuccess }) {
  const dialogRef = useRef(null);
  const submitting = useRef(false);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [removalCompleted, setRemovalCompleted] = useState(false);
  const [replacementClient, setReplacementClient] = useState(null);
  const [checkingClient, setCheckingClient] = useState(false);
  const [lookupReady, setLookupReady] = useState(false);
  const [newCbid, setNewCbid] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const oldCbid = device.cbid || device.deviceId || '';
  const controlBoardOptions = useMemo(() => {
    const boards = new Map();
    devices.forEach(item => {
      const cbid = String(item.cbid || item.deviceId || '').trim();
      if (!cbid) return;
      const sno = String(item.sno || '').trim();
      const key = cbid.toLowerCase();
      if (!boards.has(key) || sno) boards.set(key, { cbid, sno });
    });
    return [...boards.values()].sort((a, b) => a.cbid.localeCompare(b.cbid)).map(({ cbid, sno }) => ({
      value: cbid, label: sno ? `${cbid} (SNO: ${sno})` : cbid,
      isDisabled: cbid.toLowerCase() === oldCbid.toLowerCase()
    }));
  }, [devices, oldCbid]);

  useEffect(() => {
    console.log('[Device swap] Dialog opened; select a replacement CBID to call the client lookup API.');
    dialogRef.current.showModal();
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const role = String(user?.role || deviceAPI.getUserRole() || '').toLowerCase();
        const dealerId = user?.dealerId || user?.dealerid || deviceAPI.getDealerId();
        const isAdmin = role === 'admin' || role === 'superadmin';
        if (!isAdmin && !dealerId) throw new Error('Unable to identify your dealer to load control boards.');
        console.log('[Device swap] Loading boards', { api: isAdmin ? 'getalldevices' : 'getdealerdevices', role });
        const result = isAdmin ? await deviceAPI.getAllDevices() : await deviceAPI.getDealerDevices(dealerId);
        const list = Array.isArray(result) ? result : result?.data;
        if (!Array.isArray(list)) throw new Error('Unable to load control boards. Close this dialog and try again.');
        console.log('[Device swap] Boards loaded', { count: list.length });
        if (active) setDevices(list);
      } catch (requestError) {
        console.error('[Device swap] Board loading failed', requestError);
        if (active) setError(requestError.message || 'Unable to load control boards.');
      } finally {
        if (active) setLoadingDevices(false);
      }
    };
    load();
    return () => { active = false; };
  }, [user?.role, user?.dealerId, user?.dealerid]);

  useEffect(() => {
    let active = true;
    setReplacementClient(null);
    setLookupReady(false);
    setCheckingClient(false);
    if (!newCbid) return () => { active = false; };
    console.log('[Device swap] Checking selected replacement CBID', newCbid);
    setCheckingClient(true);
    deviceAPI.getClientWithCbid(newCbid).then(result => {
      if (!active) return;
      console.log('[Device swap] Client lookup complete', { cbid: newCbid, clientid: result?.clientid || null, removalRequired: Number(result?.clientid) > 0 });
      setReplacementClient(result);
      setLookupReady(true);
    }).catch(requestError => {
      console.error('[Device swap] Client lookup failed; swap is blocked', requestError);
      if (active) setError(requestError.message || 'Unable to check the selected device.');
    }).finally(() => {
      if (active) setCheckingClient(false);
    });
    return () => { active = false; };
  }, [newCbid]);

  const submit = async (event) => {
    event.preventDefault();
    console.log('[Device swap] Continue clicked', { checkingClient, lookupReady, removalCompleted });
    if (submitting.current || checkingClient || !lookupReady) {
      console.log('[Device swap] Waiting for client lookup or an existing submission.');
      return;
    }
    const replacement = newCbid.trim();
    if (!oldCbid || !replacement) {
      setError('Both current and replacement device IDs are required.');
      return;
    }
    if (oldCbid.toLowerCase() === replacement.toLowerCase()) {
      setError('Enter a replacement device ID different from the current device.');
      return;
    }
    if (!controlBoardOptions.some(option => option.value === replacement && !option.isDisabled)) {
      setError('Select a replacement control-board ID from the list.');
      return;
    }
    const fullclientid = Number(device.fullclientid || device.id);
    if (!Number.isSafeInteger(fullclientid) || fullclientid <= 0) {
      setError('The current client ID is missing. Reopen the client and try again.');
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError('');
    // SweetAlert must appear above the drawer, outside the native dialog's top layer.
    dialogRef.current.close();
    let moved = removalCompleted;
    let succeeded = false;
    try {
      const clientid = Number(replacementClient?.clientid);
      if (!moved && clientid > 0) {
        if (clientid === fullclientid) throw new Error('The selected device is already assigned to this client.');
        const confirmation = await Swal.fire({
          title: 'Replace device?',
          text: 'The client assigned to the selected replacement device will be moved to Removals before replacing the device. Continue?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue to removal date',
          cancelButtonText: 'Cancel'
        });
        if (!confirmation.isConfirmed) {
          console.log('[Device swap] Swap confirmation cancelled');
          return;
        }
        console.log('[Device swap] Starting existing log download and removal', { cbid: replacement, clientid });
        moved = await onRemove({ ...replacementClient, id: clientid, cbid: replacement });
        if (!moved) {
          console.log('[Device swap] Removal cancelled or failed; swap stopped.');
          return;
        }
        console.log('[Device swap] Replacement client moved to Removals', { clientid });
        setRemovalCompleted(true);
      }
      if (!clientid) console.log('[Device swap] No replacement client; skipping removal.');
      else if (removalCompleted) console.log('[Device swap] Removal already completed; retrying only the swap.');
      console.log('[Device swap] Sending swap request', { oldcbid: oldCbid, newcbid: replacement, fullclientid });
      Swal.fire({ title: 'Replacing device', allowOutsideClick: false, allowEscapeKey: false,
        showConfirmButton: false, didOpen: () => Swal.showLoading() });
      await deviceAPI.swapDevice({ oldcbid: oldCbid, newcbid: replacement, fullclientid });
      console.log('[Device swap] Swap succeeded');
      succeeded = true;
      onSuccess();
    } catch (requestError) {
      console.error('[Device swap] Swap stopped', requestError);
      Swal.close();
      setError(requestError.message || 'Unable to replace device. Please try again.');
    } finally {
      submitting.current = false;
      setSaving(false);
      if (!succeeded && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
    }
  };

  return (
    <dialog ref={dialogRef} className="device-swap-dialog" aria-labelledby="device-swap-title"
      onCancel={event => { event.preventDefault(); if (!saving) onClose(); }}>
      <form onSubmit={submit} aria-busy={saving}>
        <div className="device-swap-heading">
          <span className="device-swap-symbol"><ArrowLeftRight size={24} /></span>
          <div><h2 id="device-swap-title">Replace device</h2><p>Replace the current control board with a new device.</p></div>
          <button type="button" className="device-swap-close" aria-label="Close replace dialog" disabled={saving} onClick={onClose}><X size={20} /></button>
        </div>
        <div className="device-swap-fields">
          <label htmlFor="swap-current-id">Current control-board ID</label>
          <input id="swap-current-id" value={oldCbid} readOnly />
          <label htmlFor="swap-replacement-id">Replacement control-board ID</label>
          <Select inputId="swap-replacement-id" instanceId="swap-replacement" autoFocus
            className="device-swap-select" classNamePrefix="device-swap-select"
            options={controlBoardOptions} isSearchable isClearable isDisabled={saving || loadingDevices || removalCompleted}
            value={controlBoardOptions.find(option => option.value === newCbid) || null}
            placeholder={loadingDevices ? 'Loading control boards...' : 'Search by control-board ID or SNO'}
            isLoading={loadingDevices}
            noOptionsMessage={() => 'No matching control-board IDs'}
            maxMenuHeight={180} menuPlacement="auto"
            aria-invalid={Boolean(error)} aria-describedby={error ? 'swap-error' : undefined}
            onChange={option => { console.log('[Device swap] Replacement selected', option?.value || '(cleared)'); setLookupReady(false); setRemovalCompleted(false); setNewCbid(option?.value || ''); setError(''); }} />
          {/* <p id="swap-help">Search by control-board ID. The current board cannot be selected.</p> */}
          {error && <div id="swap-error" className="device-swap-error" role="alert">{error}</div>}
        </div>
        <div className="device-swap-footer">
          <button type="button" disabled={saving} onClick={onClose}>Cancel</button>
          <button type="submit" className="device-swap-submit" disabled={saving || loadingDevices || checkingClient || !lookupReady || !newCbid.trim() || !oldCbid}>
            <ArrowLeftRight size={16} />{saving ? 'Processing...' : checkingClient ? 'Checking device...' : removalCompleted ? 'Retry replacement' : 'Continue'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
